import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Badge,
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  KgInput,
  PhotoCapture,
  PickerSheet,
  ScanField,
  StatusPill,
  useToast,
} from '@/ui'
import { kgWire, type ReplaceContainerPayload } from '../commands'
import {
  bagsAt,
  placePath,
  type BagView,
  type LocalReplacement,
  type StoreState,
} from '../projection'
import { candidateMatch, checkOldTag, checkReplacementTag, compareCount } from '../rules'
import { useRefusalText } from '../components/useRefusalText'
import { CountFlow } from '../components/CountFlow'
import { StoreScreen } from '../components/StoreScreen'
import { useDraftState, useNow } from '../useStore'

interface ReplaceDraft {
  reason: 'TORN' | 'TAG_LOST'
  old_qr: string | null
  new_qr: string | null
  sack_type_id: string | null
  value_g: number | null
  photo_id: string | null
  crop_id: string | null
}

const emptyReplace = (): ReplaceDraft => ({
  reason: 'TORN',
  old_qr: null,
  new_qr: null,
  sack_type_id: null,
  value_g: null,
  photo_id: null,
  crop_id: null,
})

/**
 * F5-B1 Tag lost or bag torn. a) on the phone: count the place, then a new sack with a new tag from
 * the keeper's roll → replace_container. b) and c) are supervisor decisions in the console; the
 * phone shows each replacement's status and, for an ordinary orphan, the red-tie and move steps.
 */
export function ReplacePage() {
  const [params] = useSearchParams()
  const [counted, setCounted] = useState<{
    count_id: string
    location_id: string
    command_id: string
  } | null>(null)
  return (
    <StoreScreen title="store.replace.title">
      {(state) => {
        const loc = counted?.location_id ?? params.get('location')
        const local = loc ? state.counts.get(loc) : undefined
        const count =
          counted ??
          (local && (!params.get('count') || params.get('count') === local.count_id)
            ? {
                count_id: local.count_id,
                location_id: local.location_id,
                command_id: local.command_id,
              }
            : null)
        return (
          <div className="flex flex-col gap-4">
            <ReplacementStatus state={state} />
            {count ? (
              <ReplaceForm state={state} count={count} tripId={params.get('trip')} />
            ) : (
              <>
                <Banner tone="info">{t_countFirst}</Banner>
                <CountFlow
                  state={state}
                  initialLocationId={loc}
                  onCounted={setCounted}
                  doneLabel={<BilingualLabel k="store.replace.countDone" />}
                />
              </>
            )}
          </div>
        )
      }}
    </StoreScreen>
  )
}

// Rendered through BilingualLabel so both languages show.
const t_countFirst = <BilingualLabel k="store.replace.countFirst" />

function ReplaceForm({
  state,
  count,
  tripId,
}: {
  state: StoreState
  count: { count_id: string; location_id: string; command_id: string }
  tripId: string | null
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusalText()
  const [draft, update, loaded, clear] = useDraftState<ReplaceDraft>(
    `store.replace:${count.count_id}`,
    'store.replace',
    emptyReplace,
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const location = state.locations.get(count.location_id)
  const expected = useMemo(() => bagsAt(state, count.location_id), [state, count.location_id])
  const localCount = state.counts.get(count.location_id)
  const cmp = useMemo(
    () => compareCount(state, expected, localCount?.qr_payloads ?? []),
    [state, expected, localCount],
  )
  // A tagless bag on a short trip: the trip's missing / unscanned bags are candidates too.
  const tripCandidates: BagView[] = useMemo(() => {
    const trip = tripId ? state.trips.get(tripId) : undefined
    if (!trip) return []
    return trip.items
      .map((i) => (i.container_id ? state.bags.get(i.container_id) : undefined))
      .filter((b): b is BagView => !!b && (b.status === 'MISSING' || b.status === 'IN_TRANSIT'))
  }, [state, tripId])
  const unseen = [...cmp.unseen, ...tripCandidates]

  const old = draft.old_qr ? state.bagByPayload.get(draft.old_qr) : undefined
  const sack = state.cache.sack_types.find((s) => s.id === draft.sack_type_id)
  const newNet = draft.value_g !== null && sack ? draft.value_g - sack.tare_g : null
  const match =
    !old && newNet !== null
      ? candidateMatch(unseen, newNet, state.cache.settings.replace_match_tolerance_g)
      : null
  const crops = [
    ...new Set(
      expected.map((b) => state.lots.get(b.unit_id)?.crop_id).filter((c): c is string => !!c),
    ),
  ]
  const needCrop = !old && crops.length > 1
  const ready =
    draft.new_qr &&
    draft.sack_type_id &&
    draft.value_g &&
    draft.value_g > 0 &&
    draft.photo_id &&
    (!needCrop || draft.crop_id)

  const replace = async () => {
    if (!ready || !location) return
    setSaving(true)
    try {
      const payload: ReplaceContainerPayload = {
        count_id: count.count_id,
        location_id: location.id,
        reason: draft.old_qr ? draft.reason : 'TAG_LOST',
        old_qr_payload: draft.old_qr,
        new_container: {
          id: newId(),
          qr_payload: draft.new_qr!,
          sack_type_id: draft.sack_type_id!,
        },
        measurement: {
          id: newId(),
          value: kgWire(draft.value_g!),
          value_g: draft.value_g!,
          uom: 'KG',
          method: 'MANUAL_ENTRY',
          taken_at: new Date().toISOString(),
        },
        photo_id: draft.photo_id!,
        crop_id: needCrop ? draft.crop_id : crops.length === 1 ? (crops[0] ?? null) : null,
      }
      await enqueueCommand({
        type: 'replace_container',
        payload,
        actorId: actor.user_id,
        // depends_on the count (F5-B1.a: a count of the place is submitted first).
        parentIds: [count.command_id],
        mediaIds: [draft.photo_id!],
      })
      await clear()
      toast.success(t('store.replace.done'), t('store.replace.waitSupervisor'))
    } catch (e) {
      toast.error(t('store.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded || !location) return null
  return (
    <>
      <Card>
        <CardHeader eyebrow={placePath(state, location.id)} />
        <CardBody className="py-1">
          <ul>
            {cmp.byLot
              .filter((l) => l.short > 0)
              .map((l) => (
                <li
                  key={l.unit_id}
                  className="flex justify-between border-b border-rule/70 py-2 last:border-b-0"
                >
                  <span className="font-mono">{state.lots.get(l.unit_id)?.code}</span>
                  <Badge tone="warn">
                    {l.seen} / {l.expected} · {t('store.count.shortBy', { n: l.short })}
                  </Badge>
                </li>
              ))}
            {cmp.unseen.length === 0 && tripCandidates.length === 0 && (
              <li className="py-2 text-[14px] text-muted">{t('store.replace.noShort')}</li>
            )}
          </ul>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="flex flex-col gap-4">
          <Field
            label={<BilingualLabel k="store.replace.oldTag" />}
            hint={t('store.replace.oldTagHint')}
          >
            <ScanField
              value={draft.old_qr}
              validate={(c) => {
                const v = checkOldTag(state, c, expected)
                return v.ok
                  ? null
                  : refusal(v.reason === 'DUPLICATE' ? 'TAG_ALREADY_USED' : v.reason)
              }}
              onChange={(c) =>
                update((d) => ({ ...d, old_qr: c, reason: c ? 'TORN' : 'TAG_LOST' }))
              }
            />
          </Field>
          {!draft.old_qr && <Banner tone="warn">{t('store.replace.callSupervisor')}</Banner>}
          <Field
            label={<BilingualLabel k="store.replace.newTag" />}
            hint={t('store.replace.newTagHint')}
            required
          >
            <ScanField
              value={draft.new_qr}
              validate={(c) => {
                const r = checkReplacementTag(state, c, actor.user_id)
                return r ? refusal(r) : null
              }}
              onChange={(c) => update((d) => ({ ...d, new_qr: c }))}
            />
          </Field>
          <Field label={<BilingualLabel k="store.replace.sackType" />} required>
            <PickerSheet
              title={t('store.replace.sackType')}
              value={draft.sack_type_id}
              options={state.cache.sack_types.map((s) => ({
                value: s.id,
                label: s.name,
                meta: (
                  <span className="font-mono text-[12px] text-muted">{fmt.weight(s.tare_g)}</span>
                ),
              }))}
              onChange={(v) => update((d) => ({ ...d, sack_type_id: v }))}
            />
          </Field>
          <Field label={<BilingualLabel k="store.replace.weight" />} required>
            <KgInput
              value={draft.value_g}
              onChange={(g) => update((d) => ({ ...d, value_g: g }))}
            />
          </Field>
          {newNet !== null && (
            <div className="rounded-card border border-rule bg-paper-tint px-3 py-2 text-[14px]">
              <div>{t('store.replace.newNet', { kg: fmt.weight(newNet) })}</div>
              {old && (
                <div>
                  {t('store.receive.gateNet', { kg: fmt.weight(old.net_g_gate ?? 0) })}
                  {old.net_g_current !== null &&
                    old.net_g_current !== undefined &&
                    newNet > old.net_g_current + state.cache.settings.replace_match_tolerance_g && (
                      <span className="block font-semibold text-warn">
                        {t('store.replace.heavier')}
                      </span>
                    )}
                </div>
              )}
              {match && (
                <div
                  className={match.candidate ? 'font-semibold text-ok' : 'font-semibold text-warn'}
                >
                  {match.candidate
                    ? t('store.replace.candidate', {
                        tag: match.candidate.qr_payload,
                        lot: state.lots.get(match.candidate.unit_id)?.code ?? '',
                        kg: fmt.weight(
                          match.candidate.net_g_current ?? match.candidate.net_g_gate ?? 0,
                        ),
                      })
                    : t('store.replace.noCandidate', { n: match.matches.length })}
                </div>
              )}
            </div>
          )}
          <Field label={<BilingualLabel k="store.replace.photo" />} required>
            <PhotoCapture
              value={draft.photo_id}
              onChange={(id) => update((d) => ({ ...d, photo_id: id }))}
            />
          </Field>
          {needCrop && (
            <Field label={<BilingualLabel k="store.replace.crop" />} required>
              <PickerSheet
                title={t('store.replace.crop')}
                value={draft.crop_id}
                options={crops.map((c) => ({
                  value: c,
                  label: [...state.lots.values()].find((l) => l.crop_id === c)?.crop_name ?? c,
                }))}
                onChange={(v) => update((d) => ({ ...d, crop_id: v }))}
              />
            </Field>
          )}
          <p className="text-[13px] text-muted">{t('store.replace.cutOldTag')}</p>
          <Button
            size="xl"
            variant="accent"
            fullWidth
            disabled={!ready}
            loading={saving}
            onClick={() => void replace()}
          >
            <BilingualLabel k="store.replace.replace" />
          </Button>
        </CardBody>
      </Card>
    </>
  )
}

/** F5-B1.b / c on the phone: each replacement made here and what happened to it. */
function ReplacementStatus({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const now = useNow()
  const recent = state.replacements.filter((r) => now - r.created_at < 14 * 86_400_000)
  if (recent.length === 0) return null
  const convBay = [...state.locations.values()].find((l) => l.role === 'CONVENTIONAL')
  const step = (r: LocalReplacement) => {
    if (r.status === 'ERROR')
      return { tone: 'error' as const, text: t('store.replace.statusError') }
    if (!['APPLIED', 'DUPLICATE', 'HELD'].includes(r.status))
      return { tone: 'info' as const, text: t('store.replace.statusPending') }
    if (r.evidence === 'NONE')
      return { tone: 'warn' as const, text: t('store.replace.statusOrphan') }
    return { tone: 'info' as const, text: t('store.replace.statusReview') }
  }
  return (
    <Card>
      <CardHeader eyebrow={t('store.replace.recent')} />
      <CardBody className="flex flex-col gap-3">
        {recent.map((r) => {
          const s = step(r)
          return (
            <div
              key={r.command_id}
              className="flex flex-col gap-1.5 border-b border-rule/70 pb-3 last:border-b-0"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[14px]">
                  {r.payload.old_qr_payload ?? t('store.replace.lostTag')} →{' '}
                  {r.payload.new_container.qr_payload}
                </span>
                <StatusPill status={r.status} />
              </div>
              <span className="text-[12px] text-muted">
                {fmt.dateTime(r.created_at)}
                {r.evidence && ` · ${t(`store.replace.evidence.${r.evidence}` as const)}`}
              </span>
              <Banner tone={s.tone}>{s.text}</Banner>
              {r.evidence === 'NONE' && (
                <div className="flex gap-2">
                  <Link
                    to="/m/store/red-tie"
                    className="inline-flex min-h-touch flex-1 items-center justify-center rounded-chip border border-danger px-3 font-mono text-[12px] font-bold uppercase text-danger"
                  >
                    {t('store.redTie')}
                  </Link>
                  {convBay && (
                    <Link
                      to={`/m/store/move?to=${encodeURIComponent(convBay.id)}`}
                      className="inline-flex min-h-touch flex-1 items-center justify-center rounded-chip border border-ink px-3 font-mono text-[12px] font-bold uppercase"
                    >
                      {t('store.move')}
                    </Link>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </CardBody>
    </Card>
  )
}
